import { BODIES, type BodyId } from "@/lib/tool-kit/astronomy/planets";
import { civilFromDayNumber, dayNumber } from "@/lib/tool-kit/astronomy/zone";

/** Mean Gregorian year in days: 365.2425. Used for "years on Earth". */
export const GREGORIAN_YEAR = 365.2425;

export interface PlanetAge {
  id: BodyId;
  /** The orbit period used, in Earth days. */
  periodDays: number;
  /** Age in orbits completed, fractional. */
  age: number;
  /** Whole birthdays so far. */
  birthdays: number;
  /** Day number of the next birthday. */
  nextBirthday: number;
  daysUntil: number;
}

/** Planets with a sidereal year, in order from the Sun (the Sun and Moon have none). */
const ORBITERS = BODIES.filter(
  (body): body is typeof body & { siderealYear: number } =>
    body.siderealYear !== null
);

/** The day number of the nth birthday: n orbits after birth, to the nearest whole day. */
function birthdayDay(birth: number, period: number, n: number): number {
  return birth + Math.round(n * period);
}

function ageOn(
  id: BodyId,
  period: number,
  birth: number,
  asOf: number
): PlanetAge {
  const elapsed = asOf - birth;
  let done = Math.max(0, Math.floor(elapsed / period) - 1);
  while (birthdayDay(birth, period, done + 1) <= asOf) {
    done += 1;
  }
  const next = birthdayDay(birth, period, done + 1);
  return {
    id,
    periodDays: period,
    age: elapsed / period,
    birthdays: done,
    nextBirthday: next,
    daysUntil: next - asOf,
  };
}

/**
 * The age on each planet for two day numbers. The Earth row counts Gregorian years (its
 * birthdays follow the calendar), the others count sidereal orbits (NASA Planetary Fact Sheets). `asOf` must not precede `birth`.
 */
export function planetAges(birth: number, asOf: number): PlanetAge[] {
  return ORBITERS.map((body) => {
    if (body.id !== "earth") {
      return ageOn(body.id, body.siderealYear, birth, asOf);
    }
    // Earth's birthdays are the calendar's, not a count of 365.2425-day blocks.
    const calendar = earthCalendarAge(birth, asOf);
    return {
      ...ageOn(body.id, GREGORIAN_YEAR, birth, asOf),
      birthdays: calendar.years,
      nextBirthday: calendar.nextBirthday,
      daysUntil: calendar.daysUntil,
    };
  });
}

export interface EarthAge {
  years: number;
  months: number;
  days: number;
  nextBirthday: number;
  daysUntil: number;
}

/** Age by the calendar: whole years, months and days, and the next calendar birthday. */
export function earthCalendarAge(birth: number, asOf: number): EarthAge {
  const b = civilFromDayNumber(birth);
  const a = civilFromDayNumber(asOf);
  // Whole months lived, counting a month only once its day has come round; the day is
  // clamped to the month's length, so 31 May + 9 months is 28 February.
  let totalMonths = (a.year - b.year) * 12 + (a.month - b.month);
  if (a.day < b.day) {
    totalMonths -= 1;
  }
  const monthIndex = b.year * 12 + (b.month - 1) + totalMonths;
  const targetYear = Math.floor(monthIndex / 12);
  const targetMonth = (monthIndex % 12) + 1;
  const monthLength =
    dayNumber(
      targetMonth === 12 ? targetYear + 1 : targetYear,
      targetMonth === 12 ? 1 : targetMonth + 1,
      1
    ) - dayNumber(targetYear, targetMonth, 1);
  const anchor = dayNumber(
    targetYear,
    targetMonth,
    Math.min(b.day, monthLength)
  );
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  const days = asOf - anchor;
  // A 29 February birthday is kept on 1 March in years without one.
  const anniversary = (year: number): number => {
    const leap = dayNumber(year, 3, 1) - dayNumber(year, 2, 1) === 29;
    return b.month === 2 && b.day === 29 && !leap
      ? dayNumber(year, 3, 1)
      : dayNumber(year, b.month, b.day);
  };
  let next = anniversary(a.year);
  if (next <= asOf) {
    next = anniversary(a.year + 1);
  }
  return { years, months, days, nextBirthday: next, daysUntil: next - asOf };
}
